import UUID from 'uuid-js';
import { readOnly } from '@ember/object/computed';
import { isIn } from '@square/dashboard-utilities/macros';
import Enum from '@square/dashboard-utilities/utils/enum';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import TokenPair from '@square/dashboard-data/models/squareup/common/token/token-pair';
import { ProtoBaseModel, attr } from '@square/dashboard-data/utils/ember-model-stubs';
import { ApplicationScope } from '@square/dashboard-items-data/models/invoices/enums';
import NumberAsString from '@square/dashboard-items-data/models/attrs/number-as-string';
import DiscountSnapshot from '@square/dashboard-items-data/models/invoices/cart/discount-snapshot';

// squareup.bills.DiscountLineItem.Amounts
const Amounts = ProtoBaseModel.extend({
  appliedMoney: attr(MoneyProto)
});
const BackingType = new Enum(['ITEMS_SERVICE_DISCOUNT', 'CUSTOM_DISCOUNT']);

// squareup.bills.DiscountLineItem.BackingDetails
const BackingDetails = ProtoBaseModel.extend({
  discount: attr(DiscountSnapshot),
  backingType: attr(BackingType)
});
const Configuration = ProtoBaseModel.extend({
  variableAmountMoney: attr(MoneyProto),
  variablePercentage: attr(NumberAsString),
  clone() {
    return Configuration.deserialize(Configuration.serialize(this));
  }
});

// squareup.bills.DiscountLineItem
const DiscountLineItem = ProtoBaseModel.extend({
  writeOnlyBackingDetails: attr(BackingDetails),
  amounts: attr(Amounts),
  configuration: attr(Configuration),
  applicationScope: attr(ApplicationScope),
  discountLineItemTokenPair: attr(TokenPair),
  discountQuantity: attr(String),
  isApplicationScopeItemLevel: isIn('applicationScope', [ApplicationScope.ITEMIZATION_LEVEL, ApplicationScope.ITEMIZATION_LEVEL_PER_QUANTITY]),
  isApplicationScopeCartLevel: isIn('applicationScope', [ApplicationScope.CART_LEVEL, ApplicationScope.CART_LEVEL_FIXED]),
  backingDiscount: readOnly('writeOnlyBackingDetails.discount'),
  discountType: readOnly('backingDiscount.discountType'),
  name: readOnly('backingDiscount.name'),
  variablePercentage: readOnly('configuration.variablePercentage'),
  variableAmountMoney: readOnly('configuration.variableAmountMoney'),
  clone() {
    const discountLineItemClone = DiscountLineItem.deserialize(DiscountLineItem.serialize(this));
    discountLineItemClone.set('discountLineItemTokenPair', TokenPair.create({
      clientToken: UUID.create().toString()
    }));
    return discountLineItemClone;
  }
});
DiscountLineItem.reopenClass({
  Amounts,
  BackingDetails,
  Configuration,
  createPlaceholder() {
    return this.create({
      discountLineItemTokenPair: TokenPair.create({
        clientToken: UUID.create().toString()
      }),
      writeOnlyBackingDetails: BackingDetails.create({
        discount: DiscountSnapshot.create({
          id: UUID.create().toString(),
          percentage: 0,
          discountType: 'VARIABLE_PERCENTAGE'
        }),
        backingType: 'CUSTOM_DISCOUNT'
      }),
      configuration: Configuration.create(),
      applicationScope: ApplicationScope.CART_LEVEL
    });
  },
  createForItemLevel(discount) {
    return this.create({
      discountLineItemTokenPair: TokenPair.create({
        clientToken: UUID.create().toString()
      }),
      writeOnlyBackingDetails: BackingDetails.create({
        discount: DiscountSnapshot.createFor(discount)
      }),
      configuration: null,
      applicationScope: ApplicationScope.ITEMIZATION_LEVEL
    });
  }
});
export default DiscountLineItem;